import { useEffect, useState, useCallback } from 'react';
import { Card, Typography, List, Button, Tag, message } from 'antd';
import { LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../hooks/use_api';

const { Title, Text } = Typography;

const LINK_QUERY_PARAM = 'linked_provider';

/**
 * Lists this realm's social identity providers and whether the account is
 * connected to each. Connecting hands off to a Keycloak-hosted page (it
 * brokers the OAuth handshake with the provider) which redirects back here —
 * we mark the return trip with a `linked_provider` query param so we know to
 * refresh the list and show a result message.
 */
function LinkedAccounts() {
    const api = useApi();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyProvider, setBusyProvider] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = useCallback(() => {
        setLoading(true);
        return api.get('/api/account/security/linked-accounts')
            .then(setAccounts)
            .catch(() => message.error('Could not load linked accounts'))
            .finally(() => setLoading(false));
    }, [api]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const provider = searchParams.get(LINK_QUERY_PARAM);
        if (!provider) return;
        const error = searchParams.get('error');
        if (error) message.error(`Could not link ${provider}: ${error}`);
        else message.success(`Linked ${provider}`);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete(LINK_QUERY_PARAM);
            next.delete('error');
            return next;
        }, { replace: true });
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConnect = provider => {
        setBusyProvider(provider);
        const redirectUri = `${window.location.origin}${window.location.pathname}?${LINK_QUERY_PARAM}=${encodeURIComponent(provider)}`;
        api.get(`/api/account/security/linked-accounts/${provider}/link-url`, { params: { redirect_uri: redirectUri } })
            .then(data => { window.location.href = data.account_link_uri; })
            .catch(err => {
                message.error(err.message || `Could not start linking ${provider}`);
                setBusyProvider(null);
            });
    };

    const handleDisconnect = provider => {
        setBusyProvider(provider);
        api.del(`/api/account/security/linked-accounts/${provider}`)
            .then(() => { message.success(`Unlinked ${provider}`); return load(); })
            .catch(err => message.error(err.message || `Could not unlink ${provider}`))
            .finally(() => setBusyProvider(null));
    };

    return (
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Linked accounts</Title>
            <List
                loading={loading}
                dataSource={accounts}
                locale={{ emptyText: 'No social login providers configured' }}
                renderItem={acct => {
                    const provider = acct.providerAlias || acct.provider;
                    const busy = busyProvider === provider;
                    return (
                        <List.Item
                            actions={[
                                acct.connected ? (
                                    <Button
                                        key="disconnect"
                                        danger
                                        size="small"
                                        icon={<DisconnectOutlined />}
                                        loading={busy}
                                        onClick={() => handleDisconnect(provider)}
                                    >
                                        Disconnect
                                    </Button>
                                ) : (
                                    <Button
                                        key="connect"
                                        size="small"
                                        icon={<LinkOutlined />}
                                        loading={busy}
                                        onClick={() => handleConnect(provider)}
                                    >
                                        Connect
                                    </Button>
                                ),
                            ]}
                        >
                            <List.Item.Meta
                                title={acct.displayName || provider}
                                description={acct.connected ? (
                                    <Text type="secondary">Connected as {acct.linkedUsername}</Text>
                                ) : (
                                    <Text type="secondary">Not connected</Text>
                                )}
                            />
                            {acct.connected && <Tag color="green">Connected</Tag>}
                        </List.Item>
                    );
                }}
            />
        </Card>
    );
}

export default LinkedAccounts;
